/* eslint-disable indent */
import { useCoreContext } from '@/core';
import { CourseEntity } from '@/domains/course/entities';
import { CoursePayloadForm } from '@/domains/course/ports/payloads';
import { Button, Form, Input, InputNumber, Select, Spin, Switch } from 'antd';
import { useEffect } from 'react';
import CourseFormViewModel from '../../viewmodels/course-form.viewmodel';
import { GetListCourseLine } from '@/modules/course-lines/viewmodels';
import { GetListCourseTopic } from '@/modules/course-topic/viewmodels';

interface Props {
  detailCourse: CourseEntity;
  openForm: boolean;
  onFormSubmitSuccess?: () => void;
}

export const CourseForm = (props: Props) => {
  const { detailCourse, openForm, onFormSubmitSuccess } = props;
  const { loading } = useCoreContext();
  const [form] = Form.useForm();
  const { actionSaveCourse } = CourseFormViewModel();
  const {
    courseLineData,
    actionGetAllCourseLine,
    loading: loadingCourseLine,
  } = GetListCourseLine();

  const {
    courseTopicData,
    actionGetAllCourseTopic,
    loading: loadingCourseTopic,
  } = GetListCourseTopic();

  // Load course lines and course topics on component mount
  useEffect(() => {
    actionGetAllCourseLine();
    actionGetAllCourseTopic();
  }, []);

  useEffect(() => {
    if (detailCourse && detailCourse.id) {
      form.setFieldsValue({
        id: detailCourse.id,
        name: detailCourse.name,
        shortName: detailCourse.shortName,
        description: detailCourse.description,
        isActive: detailCourse.isActive,
        numberOfSession: detailCourse.numberOfSession,
        sessionHour: detailCourse.sessionHour,
        optional: detailCourse.optional,
        courseLineForUpdate: detailCourse.courseLineForUpdate,
        courseTopic: detailCourse.courseTopic,
        courseLine: detailCourse.courseLine,
      });
    } else {
      form.resetFields();
    }
  }, [detailCourse, form, openForm]);

  const onFinish = async (values: CoursePayloadForm) => {
    const success = await actionSaveCourse({
      id: detailCourse?.id,
      ...values,
    });

    if (success) {
      // Reset form after successful submission
      form.resetFields();

      // Notify parent component about successful submission
      if (onFormSubmitSuccess) {
        onFormSubmitSuccess();
      }
    }
  };

  if (loading || loadingCourseLine || loadingCourseTopic) {
    return <Spin />;
  }

  // Format Vietnamese number
  const formatNumber = (val: number | string | undefined): string => {
    if (val === undefined || val === null) return '';
    if (typeof val === 'string') val = Number(val);
    try {
      return val.toLocaleString('vi-VN');
    } catch (e) {
      return String(val);
    }
  };

  // Parse Vietnamese formatted number back to number
  const parseNumber = (val: string | undefined): number => {
    if (!val) return 0;
    return Number(val.replace(/\./g, ''));
  };

  return (
    <Form
      form={form}
      name='course-form'
      onFinish={onFinish}
      layout='vertical'
      initialValues={{
        isActive: true,
        optional: true,
        courseLineForUpdate: [],
        courseTopic: '',
        courseLine: '',
      }}
    >
      <Form.Item
        name='name'
        label='Name'
        rules={[{ required: true, message: 'Please input the name!' }]}
      >
        <Input placeholder='Enter name' />
      </Form.Item>

      <Form.Item
        name='shortName'
        label='Short Name'
        rules={[{ required: true, message: 'Please input the short name!' }]}
      >
        <Input.TextArea rows={4} placeholder='Enter description' />
      </Form.Item>

      <Form.Item
        name='description'
        label='Description'
        rules={[{ required: true, message: 'Please input the description!' }]}
      >
        <Input.TextArea rows={4} placeholder='Enter description' />
      </Form.Item>

      <Form.Item
        name='numberOfSession'
        label='Number of Session'
        rules={[{ required: true, message: 'Please input the number of session!' }]}
      >
        <InputNumber min={0} style={{ width: '100%' }} placeholder='Enter number of session' />
      </Form.Item>

      <Form.Item
        name='sessionHour'
        label='Session Hour'
        rules={[{ required: true, message: 'Please input the session hour!' }]}
      >
        <InputNumber
          min={0}
          style={{ width: '100%' }}
          formatter={formatNumber}
          parser={(value) => parseNumber(value)}
          placeholder='Enter session hour'
        />
      </Form.Item>

      <Form.Item name='optional' label='Course Type' valuePropName='checked'>
        <Switch checkedChildren='Optional' unCheckedChildren='Required' />
      </Form.Item>

      <Form.Item name='isActive' label='Status' valuePropName='checked'>
        <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
      </Form.Item>

      <Form.Item name='courseLineForUpdate' label='Course Line For Update'>
        <Select
          mode='multiple'
          placeholder='Select course line for update'
          optionFilterProp='children'
          style={{ width: '100%' }}
        >
          {courseLineData && courseLineData.data
            ? courseLineData.data.map((courseLine) => (
                <Select.Option key={courseLine.id} value={courseLine.id}>
                  {courseLine.name}
                </Select.Option>
              ))
            : []}
        </Select>
      </Form.Item>

      <Form.Item
        name='courseLine'
        label='Course Line'
        rules={[{ required: true, message: 'Please select a course line!' }]}
      >
        <Select placeholder='Select course line' style={{ width: '100%' }}>
          {courseLineData && courseLineData.data
            ? courseLineData.data.map((courseLine) => (
                <Select.Option key={courseLine.id} value={courseLine.id}>
                  {courseLine.name}
                </Select.Option>
              ))
            : []}
        </Select>
      </Form.Item>

      <Form.Item name='courseTopic' label='Course Topic'>
        <Select placeholder='Select course topic' style={{ width: '100%' }}>
          {courseTopicData && courseTopicData.data
            ? courseTopicData.data.map((courseTopic) => (
                <Select.Option key={courseTopic.id} value={courseTopic.id}>
                  {courseTopic.name}
                </Select.Option>
              ))
            : []}
        </Select>
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          {detailCourse?.id ? 'Update' : 'Create'}
        </Button>
      </Form.Item>
    </Form>
  );
};
